<template>
  <Teleport to="body">
    <div
      v-if="showPortfolioTrackerModal"
      ref="modalRoot"
      class="portfolio-tracker-modal"
      :class="{
        'portfolio-tracker-modal--desktop': isDesktop,
        'portfolio-tracker-modal--mobile': !isDesktop
      }"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="headingId"
    >
      <div
        v-if="isDesktop"
        class="portfolio-tracker-modal__backdrop"
        @click="closePortfolioTrackerModal"
      />
      <div
        class="portfolio-tracker-modal__panel"
        :style="isDesktop ? panelStyle : null"
      >
        <div
          v-if="isDesktop"
          class="portfolio-tracker-modal__arrow"
          aria-hidden="true"
        />
        <div class="portfolio-tracker-modal__panel-body">
          <button
            type="button"
            class="btn btn-link portfolio-tracker-modal__close"
            @click="closePortfolioTrackerModal"
            :aria-label="content.closeAriaLabel"
          >
            <FontAwesomeIcon :icon="appIcons.faXmark" />
          </button>

          <!-- Single DOM in mobile-natural order (heading → status → gauge →
               image → options → legal). Desktop reorders via CSS Grid
               `grid-template-areas` on `&--desktop .__panel-body` — see the
               style block below — putting the image in a left column and
               legal spanning both. Avoids duplicating the subtree per
               viewport. -->
          <h1
            :id="headingId"
            class="portfolio-tracker-modal__heading"
          >
            {{ content.heading }}
          </h1>
          <PortfolioTrackerStatusPanel
            v-if="displayStage !== null"
            :status-sentence="statusSentence"
            :arrival-date-text="arrivalDateText"
            :tracking-link="trackingLink"
            :track-link-label="content.trackLinkLabel"
            class="portfolio-tracker-modal__status"
          />
          <ProgressGauge
            v-if="displayStage !== null"
            :display-stage="displayStage"
            :label-preparing="content.gaugeLabelPreparing"
            :label-shipped="content.gaugeLabelShipped"
            :label-out-for-delivery="content.gaugeLabelOutForDelivery"
            :label-delivered="content.gaugeLabelDelivered"
            class="portfolio-tracker-modal__gauge"
          />
          <img
            :src="content.productImageUrl"
            :alt="content.productImageAlt"
            class="portfolio-tracker-modal__product-image"
          />
          <PortfolioTrackerOptionsPanel
            :heading="content.optionsHeading"
            :options="trackerOptions"
            class="portfolio-tracker-modal__options"
          />
          <p class="portfolio-tracker-modal__legal">{{ content.legalCopy }}</p>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts">
import { defineComponent, nextTick, ref, watch } from 'vue'
import { useMq } from 'vue3-mq'

import { useAppContentStore } from '@/stores/appContent'
import { useMainStore } from '@/stores/main'
import { mapActions, mapState, mapStores } from 'pinia'

import { onKeyStroke, useEventListener } from '@vueuse/core'
import dayjs from 'dayjs'

import useAppIcons from '@/composables/appIcons'
import { useFocusTrap } from '@/composables/useFocusTrap'
import { PortfolioTrackerDisplayStage, resolveDisplayStage } from '@/lib/portfolioTrackerDisplay'

import {
  resolveTrackerContent,
  resolveTrackerOptions,
  substitute,
  type TrackerContent,
  type TrackerOption
} from '@/components/Senior/PortfolioTracker/content'
import PortfolioTrackerOptionsPanel from '@/components/Senior/PortfolioTracker/PortfolioTrackerOptionsPanel.vue'
import PortfolioTrackerStatusPanel from '@/components/Senior/PortfolioTracker/PortfolioTrackerStatusPanel.vue'
import ProgressGauge from '@/components/Senior/PortfolioTracker/ProgressGauge.vue'

// Horizontal offset between the page header container's right edge and the
// panel's right edge. Positive value pushes the panel slightly past the
// container edge to match the Figma's visual overhang.
const PANEL_RIGHT_OFFSET_PX = 0
// Vertical offset from viewport top to the panel's top edge — just below the
// top header bar.
const PANEL_TOP_PX = 70

export default defineComponent({
  name: 'PortfolioTrackerModal',
  components: { PortfolioTrackerOptionsPanel, PortfolioTrackerStatusPanel, ProgressGauge },
  setup() {
    const main = useMainStore()
    onKeyStroke('Escape', () => {
      if (main.showPortfolioTrackerModal) {
        main.closePortfolioTrackerModal()
      }
    })
    const modalRoot = ref<HTMLElement | null>(null)
    useFocusTrap(modalRoot, () => main.showPortfolioTrackerModal)

    // Panel's right edge anchors to the page-header container's right edge
    // (`.cady-header-container`) so the panel respects the same horizontal
    // extent as the top bar across viewport widths. The arrow then attaches
    // to the panel's top-right via static CSS (`top: -14px; right: <fixed>`),
    // so the arrow automatically tracks the panel — no separate measurement
    // needed.
    const panelStyle = ref<Record<string, string>>({ top: `${PANEL_TOP_PX}px`, right: '16px' })

    const measurePanelAnchor = () => {
      const containerEl = document.querySelector<HTMLElement>('.cady-header-container')
      if (containerEl === null) return
      const r = containerEl.getBoundingClientRect()
      if (r.width === 0) return
      // Use `clientWidth` (layout viewport, scrollbar excluded) rather than
      // `window.innerWidth` (visual viewport, scrollbar included). The
      // container's `r.right` is always in scrollbar-excluded coordinates,
      // so pairing it with `clientWidth` makes the math consistent whether
      // or not the page's vertical scrollbar is currently visible. Without
      // this, auto-open during PLP load measured before the modal's
      // `body.overflow = 'hidden'` watch had hidden the scrollbar, inflating
      // the offset by the scrollbar width (~15px) and pushing the panel left.
      const viewportRight = document.documentElement.clientWidth
      const rightCss = Math.max(viewportRight - r.right - PANEL_RIGHT_OFFSET_PX, 16)
      panelStyle.value = {
        top: `${PANEL_TOP_PX}px`,
        right: `${rightCss}px`
      }
    }

    // Re-measure across multiple timing windows on modal-open so we catch
    // late-mounted layouts. The modal mounts at App.vue's level — sibling to
    // RouterView — so the route's layout (including `CadyHeader`) may not be
    // in the DOM yet by the time `nextTick` or even the next animation frame
    // fires. A `setTimeout(100)` catches that case as belt-and-suspenders.
    watch(
      () => main.showPortfolioTrackerModal,
      (open) => {
        if (!open) return
        measurePanelAnchor()
        nextTick(measurePanelAnchor)
        requestAnimationFrame(measurePanelAnchor)
        setTimeout(measurePanelAnchor, 100)
      },
      { immediate: true }
    )

    // Keep the panel aligned across viewport changes.
    useEventListener(window, 'resize', measurePanelAnchor, { passive: true })

    return {
      ...useAppIcons(),
      mq$: useMq(),
      panelStyle,
      modalRoot
    }
  },
  computed: {
    ...mapStores(useAppContentStore),
    ...mapState(useMainStore, ['showPortfolioTrackerModal', 'portfolioTrackerDetail']),
    content(): TrackerContent {
      return resolveTrackerContent(this.appContentStore.seniorPortfolioTrackerContent)
    },
    trackerOptions(): TrackerOption[] {
      return resolveTrackerOptions(this.content)
    },
    isDesktop(): boolean {
      return this.mq$.mdPlus
    },
    headingId(): string {
      return `portfolio-tracker-modal-heading-${this.$.uid}`
    },
    displayStage(): PortfolioTrackerDisplayStage | null {
      if (this.portfolioTrackerDetail === null) return null
      return resolveDisplayStage(this.portfolioTrackerDetail.StatusId)
    },
    statusSentence(): string {
      switch (this.displayStage) {
        case PortfolioTrackerDisplayStage.Preparing:
          return this.content.statusSentencePreparing
        case PortfolioTrackerDisplayStage.Shipped:
          return this.content.statusSentenceShipped
        case PortfolioTrackerDisplayStage.OutForDelivery:
          return this.content.statusSentenceOutForDelivery
        case PortfolioTrackerDisplayStage.Delivered:
          return this.content.statusSentenceDelivered
        case PortfolioTrackerDisplayStage.Billed:
          return this.content.statusSentenceBilled
        case PortfolioTrackerDisplayStage.Returned:
          return this.content.statusSentenceReturned
      }
      return ''
    },
    // The Returned state shows return-tracking info; all other applicable
    // states use outbound tracking. Null when no tracking is populated.
    relevantTracking() {
      if (this.portfolioTrackerDetail === null) return null
      return this.displayStage === PortfolioTrackerDisplayStage.Returned
        ? this.portfolioTrackerDetail.ReturnTracking
        : this.portfolioTrackerDetail.Tracking
    },
    trackingLink(): string | null {
      return this.relevantTracking?.TrackingLink ?? null
    },
    arrivalDateText(): string | null {
      // The "Will arrive on…" sentence only makes sense for in-motion states
      // where the date is a future estimate. Suppress for Delivered (already
      // arrived) and for any terminal state where the date is historical.
      if (this.displayStage === PortfolioTrackerDisplayStage.Delivered) return null
      const date = this.relevantTracking?.EstimatedDeliveryDate
      if (date === null || date === undefined) return null
      const formatted = dayjs(date).format('ddd, MMM D, YYYY')
      return substitute(this.content.arrivalDateTemplate, {
        PortfolioArrivalDate: formatted
      })
    }
  },
  methods: {
    ...mapActions(useMainStore, ['closePortfolioTrackerModal'])
  },
  watch: {
    showPortfolioTrackerModal: {
      immediate: true,
      handler(open: boolean) {
        if (typeof document !== 'undefined') {
          document.body.style.overflow = open ? 'hidden' : ''
        }
      }
    }
  },
  beforeUnmount() {
    if (typeof document !== 'undefined') {
      document.body.style.overflow = ''
    }
  }
})
</script>

<style scoped lang="scss">
.portfolio-tracker-modal {
  position: fixed;
  inset: 0;
  z-index: 1050;

  &__backdrop {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
  }

  &__panel {
    position: relative;
    background: #fff;
  }

  &__close {
    position: absolute;
    top: 1rem;
    right: 1rem;
    color: #000;
    font-size: 1.25rem;
    padding: 0;
    z-index: 1;
    line-height: 1;
  }

  &__heading {
    font-size: 1.75rem;
    font-weight: 700;
    margin: 0 0 0.75rem;
    color: #000;
  }

  &__status {
    margin-bottom: 1rem;
  }

  &__gauge {
    margin-bottom: 1.5rem;
  }

  &__options {
    margin-bottom: 1rem;
  }

  &__legal {
    font-size: 0.875rem;
    color: #555;
    margin: 0;
    padding-top: 1.5rem;
  }

  // Portfolio product photo — stand-in URL from placehold.co until the asset
  // lands in Contentful.
  &__product-image {
    display: block;
    width: 100%;
    aspect-ratio: 369 / 264;
    object-fit: cover;
    border-radius: 8px;
  }

  // Desktop: flyout panel anchored to the cart icon in the top-right with a
  // triangular arrow pointing up to it. Backdrop dims the rest of the page.
  // The panel's right edge tracks the page-header container's right edge
  // (set inline via `panelStyle`); the arrow attaches to the panel's
  // top-right via static CSS so it automatically follows the panel without a
  // separate measurement. Adjust `right` on `__arrow` if the cart icon's
  // horizontal offset inside the container changes.
  &--desktop {
    display: block;

    // Panel position (top/right) is set inline via `:style="panelStyle"`.
    // The panel itself uses `overflow: visible` so the arrow positioned at
    // `top: -14px` isn't clipped; scrolling is delegated to `__panel-body`.
    .portfolio-tracker-modal__panel {
      position: absolute;
      max-width: 864px;
      width: calc(100vw - 32px);
      overflow: visible;
      border-radius: 12px;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
      background: #fff;
    }

    .portfolio-tracker-modal__arrow {
      position: absolute;
      top: -14px;
      right: 16px;
      width: 0;
      height: 0;
      border-left: 14px solid transparent;
      border-right: 14px solid transparent;
      border-bottom: 14px solid #fff;
      z-index: 2;
    }

    // Two-column layout: image-left column (fixed 369px) + content-right
    // column (flexible), with `legal` spanning both at the bottom. Single
    // DOM order (mobile-natural) is reused by both viewports — desktop
    // remaps the children via named grid areas.
    .portfolio-tracker-modal__panel-body {
      max-height: calc(100vh - 100px);
      overflow-y: auto;
      padding: 2rem 2.5rem;
      border-radius: 12px;

      display: grid;
      grid-template-columns: 369px 1fr;
      column-gap: 1.5rem;
      grid-template-areas:
        'image heading'
        'image status'
        'image gauge'
        'image options'
        'legal legal';
      align-items: start;
    }

    .portfolio-tracker-modal__heading {
      grid-area: heading;
    }

    .portfolio-tracker-modal__status {
      grid-area: status;
    }

    .portfolio-tracker-modal__gauge {
      grid-area: gauge;
    }

    .portfolio-tracker-modal__product-image {
      grid-area: image;
    }

    .portfolio-tracker-modal__options {
      grid-area: options;
    }

    .portfolio-tracker-modal__legal {
      grid-area: legal;
    }
  }

  // Mobile: full-page modal, no backdrop (the modal itself is the screen).
  &--mobile {
    background: #fff;

    .portfolio-tracker-modal__panel {
      width: 100%;
      height: 100%;
      padding: 1.25rem;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .portfolio-tracker-modal__heading {
      padding-right: 2rem; // give the close X room
    }

    .portfolio-tracker-modal__product-image {
      margin-bottom: 1.25rem;
    }

    .portfolio-tracker-modal__legal {
      margin-top: auto;
    }
  }
}
</style>
